import { useContext, useEffect, useMemo } from 'react';
import { CurrentMembershipPermissionsType } from '@identity/graphql';
import { CurrentMembershipContext } from '@identity/contexts';
import { ErrorHandler } from '@common/components';
import { CurrentMembershipType } from '@identity/types';

export const useCurrentMembership = ({
  hideError = false,
}: {
  hideError?: boolean;
} = {}) => {
  const membership = useContext<CurrentMembershipPermissionsType | null>(
    CurrentMembershipContext
  );

  const transformedPermissions = useMemo(
    () => transformPermissions(membership),
    [membership]
  );

  useEffect(() => {
    if (!transformedPermissions && !hideError) {
      ErrorHandler.showErrorNotification();
    }
  }, [hideError, transformedPermissions]);

  if (!transformedPermissions) {
    return {} as CurrentMembershipPermissionsType;
  }

  return transformedPermissions;
};

// FYI: This is to avoid having to use the .value every time.
const transformPermissions = (membership: CurrentMembershipType | null) => {
  if (!membership) return null;

  const transformedEntries = Object.entries(membership).map(
    ([key, permission]) => {
      const hasPermissionAValueProperty =
        key.startsWith('can') &&
        permission &&
        typeof permission === 'object' &&
        'value' in permission;

      return [key, hasPermissionAValueProperty ? permission.value : permission];
    }
  );
  return Object.fromEntries(
    transformedEntries
  ) as CurrentMembershipPermissionsType;
};
